/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import SettingsIcon from '@mui/icons-material/esm/Settings';
import ShareIcon from '@mui/icons-material/esm/Share';
import IntegrationIcon from '@mui/icons-material/esm/IntegrationInstructions';
import FavoriteIcon from '@mui/icons-material/esm/Favorite';
import { Divider, IconButton, ListItemIcon, ListItemText, Popover, SvgIcon, Badge, styled } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { withStyles } from '@mui/styles';
import React, { useState } from 'react';
import MoreVertIcon from '@mui/icons-material/esm/MoreVert';
import BallotIcon from '@mui/icons-material/esm/Ballot';
import { FormattedMessage } from 'react-intl';
import CustomTooltip from '../utils/CustomTooltip';
import { PluginSettingItems } from '@cedalo/webui-extensions';
import { withAppData } from './AppProvider';
import { withUser } from '../../UserProvider';
import { useLocalState } from './GraphQLWSClient';
import { addToFavorites, removeFromFavorites } from './AppGraphQL';
import { withScopeRights } from '../utils/with';

const ConnectionIcon = withAppData(({ connections }) => ({
	hasError: connections.some((c) => c.status && !c.status.isConnected)
}))(({ hasError }) => (
	<Badge color="error" variant="dot" invisible={!hasError}>
		<SvgIcon>
			<path
				fill="currentColor"
				d="M21.4 7.5C22.2 8.3 22.2 9.6 21.4 10.3L18.6 13.1L10.8 5.3L13.6 2.5C14.4 1.7 15.7 1.7 16.4 2.5L18.2 4.3L21.2 1.3L22.6 2.7L19.6 5.7L21.4 7.5M15.6 13.3L14.2 11.9L11.4 14.7L9.3 12.6L12.1 9.8L10.7 8.4L7.9 11.2L6.4 9.8L3.6 12.6C2.8 13.4 2.8 14.7 3.6 15.4L5.4 17.2L1.4 21.2L2.8 22.6L6.8 18.6L8.6 20.4C9.4 21.2 10.7 21.2 11.4 20.4L14.2 17.6L12.8 16.2L15.6 13.3Z"
			/>
		</SvgIcon>
	</Badge>
));

const FavoriteIconColored = styled(FavoriteIcon)(({ theme }) => ({
	color: theme.palette.primary.main
}));

function AppActionsComponent(props) {
	const [anchorEl, setAnchorEl] = useState(null);
	const { appEditable, appId, rights } = props;
	const [isFavorite, setFavorite] = useLocalState(props.isFavorite);

	const handleSettingsMenu = (event) => {
		setAnchorEl(anchorEl ? null : event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};

	const toggleFavorite = async () => {
		try {
			setFavorite(!isFavorite);
			if (isFavorite) {
				await removeFromFavorites(appId);
			} else {
				await addToFavorites(appId);
			}
		} catch (error) {
			console.error(error);
		}
	};

	const open = Boolean(anchorEl);
	return (
		<div
			data-tour="app-actions"
			style={{
				alignItems: 'end',
				display: 'flex',
				minHeight: '10px',
				marginTop: '5px'
			}}
		>
			<div style={{ display: 'flex' }}>
				<CustomTooltip
					header={isFavorite ? 'Tooltip.RemoveToFavoritesTitle' : 'Tooltip.AddToFavoritesTitle'}
					message={isFavorite ? 'Tooltip.RemoveToFavoritesMessage' : 'Tooltip.AddToFavoritesMessage'}
				>
					<div
						style={{
							marginLeft: '15px',
							display: 'inline'
						}}
					>
						<IconButton onClick={toggleFavorite} size="small">
							{isFavorite ? <FavoriteIconColored /> : <FavoriteIcon />}
						</IconButton>
					</div>
				</CustomTooltip>
				<CustomTooltip header="Tooltip.MachineIntegrationTitle" message="Tooltip.MachineIntegrationMessage">
					<div
						style={{
							marginLeft: '15px',
							display: 'inline'
						}}
					>
						<IconButton
							disabled={!appEditable || !rights.includes('account.view')}
							onClick={() => {
								return props.showDialog('Integrations');
							}}
							size="small"
						>
							<IntegrationIcon />
						</IconButton>
					</div>
				</CustomTooltip>
				<CustomTooltip header="Tooltip.ConnectionsTitle" message="Tooltip.ConnectionsMessage">
					<div
						style={{
							marginLeft: '15px',
							display: 'inline'
						}}
					>
						<IconButton
							data-tour="button-connections"
							onClick={() => props.showDialog('Connections')}
							size="small"
							disabled={!appEditable}
						>
							<ConnectionIcon />
						</IconButton>
					</div>
				</CustomTooltip>
				<CustomTooltip header="Tooltip.AppShareTitle" message="Tooltip.AppShareMessage">
					<div style={{ marginLeft: '15px', display: 'inline' }}>
						<IconButton
							onClick={() => {
								props.showDialog('ShareSettings');
								props.showViewMode(true);
							}}
							size="small"
						>
							<ShareIcon />
						</IconButton>
					</div>
				</CustomTooltip>
				<CustomTooltip header="Tooltip.AppSettingsTitle" message="Tooltip.AppSettingsMessage">
					<div
						style={{
							marginLeft: '15px',
							display: 'inline'
						}}
					>
						<IconButton
							data-tour="app-menu"
							onClick={handleSettingsMenu}
							size="small"
							disabled={!appEditable}
						>
							<MoreVertIcon />
						</IconButton>
					</div>
				</CustomTooltip>
				<Popover
					anchorEl={anchorEl}
					id="account-menu"
					open={open}
					onClose={handleClose}
					onClick={handleClose}
					PaperProps={{
						elevation: 0,
						sx: {
							overflow: 'visible',
							filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
							mt: 1.5,
							'&:before': {
								content: '""',
								display: 'block',
								position: 'absolute',
								top: 0,
								right: 14,
								width: 10,
								height: 10,
								bgcolor: 'background.paper',
								transform: 'translateY(-50%) rotate(45deg)',
								zIndex: 0
							}
						}
					}}
					transformOrigin={{ horizontal: 'right', vertical: 'top' }}
					anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
				>
					<MenuItem dense onClick={() => props.showDialog('AppSettings')}>
						<ListItemIcon>
							<SettingsIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText>
							<FormattedMessage id="AppActions.AppSettings" defaultMessage="App Settings" />
						</ListItemText>
					</MenuItem>
					<PluginSettingItems onClick={(id, data) => props.showDialog(id, data)} />
					<Divider />
					<MenuItem dense onClick={() => props.showDialog('NameSettings')}>
						<ListItemIcon>
							<IconButton size="small">
								<SvgIcon>
									<path
										// eslint-disable-next-line max-len
										d="M21.4 11.6L12.4 2.6C12 2.2 11.5 2 11 2H4C2.9 2 2 2.9 2 4V11C2 11.5 2.2 12 2.6 12.4L11.6 21.4C12 21.8 12.5 22 13 22C13.5 22 14 21.8 14.4 21.4L21.4 14.4C21.8 14 22 13.5 22 13C22 12.5 21.8 12 21.4 11.6M13 20L4 11V4H11L20 13M6.5 5C7.3 5 8 5.7 8 6.5S7.3 8 6.5 8 5 7.3 5 6.5 5.7 5 6.5 5M10.1 8.9L11.5 7.5L17 13L15.6 14.4L10.1 8.9M7.6 11.4L9 10L13 14L11.6 15.4L7.6 11.4Z"
									/>
								</SvgIcon>
							</IconButton>
						</ListItemIcon>
						<ListItemText>
							<FormattedMessage id="AppActions.Names" defaultMessage="Edit Names" />
						</ListItemText>
					</MenuItem>
					<MenuItem dense onClick={() => props.showDialog('ShapeListSettings')}>
						<ListItemIcon>
							<BallotIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText>
							<FormattedMessage id="AppActions.ShapeList" defaultMessage="Show Shape List" />
						</ListItemText>
					</MenuItem>
				</Popover>
			</div>
		</div>
	);
}

export const AppActions = withScopeRights(
	withUser(({ user }, { appId }) => ({ isFavorite: user.favorites.includes(appId) }))(
		withStyles({}, { withTheme: true })(AppActionsComponent)
	)
);
